import React, { useState } from "react";
import {
  TextField,
  Button,
  Container,
  Box,
  useMediaQuery,
} from "@mui/material";
import SnackBar from "./SnackBar";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { add_template, get_fillers, get_profile_data } from "../Api service/APIvariables";
import { height } from "@mui/system";
import { useNavigate } from "react-router-dom";

const Template = () => {
  const navigate = useNavigate();
  const [template_name, setTemplateName] = useState("");
  const [subject, setSubject] = useState("");
  const [richContent, setRichContent] = useState(""); // Quill rich text
  const isMobile = useMediaQuery("(max-width: 768px)"); // Detect mobile screens

  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [planRequired, setPlanRequired] = useState(1);
  const [isAdmin, setIsAdmin] = useState(false);

  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(false);
  const [color, setColor] = useState(false);
  const [message, setMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [fillersList, setFillersList] = useState([]);

  React.useEffect(() => {
    try {
      const authStr = localStorage.getItem("auth");
      if (authStr && authStr !== "true") {
        const authObj = JSON.parse(authStr);
        if (authObj.role === "admin") {
          setIsAdmin(true);
        }
      }
    } catch(e) {}
    fetchFillersForMentions();
  }, []);

  const fetchFillersForMentions = async () => {
    try {
      const user_id = localStorage.getItem("user_id");
      const token = localStorage.getItem("auth_token");
      if (!user_id || !token) return;

      const formData = new FormData();
      formData.append("user_id", user_id);

      // Fetch Profile
      let sysName = "Your Profile Name";
      let sysEmail = "Your Profile Email";
      let sysMobile = "Your Profile Mobile";
      let sysCompany = "Your Profile Company";

      try {
        const profileRes = await fetch(get_profile_data, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const profileResult = await profileRes.json();
        if (!profileResult.error && profileResult.data) {
          sysName = profileResult.data.name || sysName;
          sysEmail = profileResult.data.email || sysEmail;
          sysMobile = profileResult.data.mobile || sysMobile;
          sysCompany = profileResult.data.company_name || sysCompany;
        }
      } catch (e) {}

      // Fetch Custom Fillers
      const fillersRes = await fetch(get_fillers, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const fillersResult = await fillersRes.json();
      
      const isAdmin = window.location.pathname.startsWith("/admin");

      if (!fillersResult.error && fillersResult.data) {
        const planRank = fillersResult.plan_rank || 1;
        const validFillers = fillersResult.data.filter(f => 
           isAdmin || f.is_system == 0 || f.plan_required <= planRank
        );
        const mappedFillers = validFillers.map((f) => ({
          id: f.filler_key,
          value: f.filler_key,
          denotationChar: "{{"
        }));
        setFillersList(mappedFillers);
      } else {
        setFillersList([]);
      }
    } catch (e) {}
  };

  const quillRef = React.useRef(null);
  const [suggestionQuery, setSuggestionQuery] = useState(null);
  const [cursorPos, setCursorPos] = useState({ top: 0, left: 0 });
  const [filteredFillers, setFilteredFillers] = useState([]);

  React.useEffect(() => {
    if (suggestionQuery !== null) {
      const filtered = fillersList.filter((f) => 
        f.value.toLowerCase().includes(suggestionQuery.toLowerCase())
      );
      setFilteredFillers(filtered);
    }
  }, [suggestionQuery, fillersList]);

  const handleEditorChange = (content, delta, source, editor) => {
    setRichContent(content);
    if (source === 'user') {
      const selection = editor.getSelection();
      if (selection && quillRef.current) {
        const index = selection.index;
        const textBefore = editor.getText(0, index);
        // Look for '{' followed by letters/numbers at the end of the string
        const match = textBefore.match(/\{([A-Za-z0-9_]*)$/);
        
        if (match) {
          setSuggestionQuery(match[1]);
          const bounds = editor.getBounds(index);
          // Set dropdown position just below the cursor and store the exact index
          setCursorPos({ top: bounds.top + bounds.height + 10, left: bounds.left, index: index });
        } else {
          setSuggestionQuery(null);
        }
      }
    }
  };

  const handleSelectSuggestion = (fillerValue) => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      
      // Use the stored cursor position instead of querying the blurred editor
      const index = cursorPos.index || editor.getLength();
      const textBefore = editor.getText(0, index);
      const match = textBefore.match(/\{([A-Za-z0-9_]*)$/);
      
      if (match) {
        // Delete the trigger and query typed so far
        editor.deleteText(index - match[0].length, match[0].length);
        // Insert the final {{Filler}}
        editor.insertText(index - match[0].length, `{{${fillerValue}}}`);
        // Restore focus to the editor after insertion
        editor.setSelection(index - match[0].length + `{{${fillerValue}}}`.length);
      } else {
        editor.insertText(index, `{{${fillerValue}}}`);
      }
      
      setSuggestionQuery(null);
      setRichContent(editor.root.innerHTML);
    }
  };

  const modules = React.useMemo(() => ({
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ]
  }), []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!template_name || !subject || !richContent) {
      setMessage("All fields are required!");
      setOpen(true);
      setColor(false);
      setStatus(false);
      return;
    }

    setLoading(true);

    // Prepare form data with FormData and append necessary fields
    const formData = new FormData();
    formData.append("user_id", localStorage.getItem("user_id"));
    formData.append("template_name", template_name);
    formData.append("subject", subject);
    formData.append("message", richContent); 
    formData.append("is_system", isAdvancedMode ? 1 : 0);
    formData.append("plan_required", isAdvancedMode ? planRequired : 0);

    const token = localStorage.getItem("auth_token");

    if (!token) {
      setMessage("Token is missing or invalid!");
      setOpen(true);
      setColor(false);
      setStatus(false);
      setLoading(false);
      return;
    }

    try {
      // API call using fetch with FormData and Authorization header
      const response = await fetch(add_template, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`, // Pass token in the header
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok && !data.error) {
        setMessage("Template created successfully!");
        setOpen(true);
        setStatus(true);
        setColor(true);
        setTemplateName("");
        setSubject("");
        setRichContent(""); 
        
        // Redirect to template list after a short delay so they can see the success toast
        setTimeout(() => {
          let role = "user";
          try {
            const authStr = localStorage.getItem("auth");
            if (authStr && authStr !== "true") {
              const authObj = JSON.parse(authStr);
              if (authObj.role) role = authObj.role;
            }
          } catch(e) {}
          
          if (role === "admin") {
            navigate("/admin/template");
          } else {
            navigate("/");
          }
        }, 1500);
      } else {
        setMessage(`Error: ${data?.message || data?.mysql_error || "Unknown error"}`);
        setOpen(true);
        setStatus(false);
        setColor(false);
      }
    } catch (error) {
      setMessage(`Error during submission: ${error.message}`);
      setColor(false);
      setOpen(true);
      setStatus(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="template"
      style={{
        display: "block",
        // backgroundColor: "#eceff1",
        minHeight: "100vh",
        // padding: "20px",
        position: "relative",
        width: "100%",
        // maxWidth: "98%",
        margin: "0 auto",
        // borderRadius: "30px",
        // paddingRight: "20px",
        paddingLeft: "20px",
        paddingRight: isMobile ? "15px" : "0px",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <SnackBar
        open={open}
        message={message}
        setOpen={setOpen}
        status={status}
        color={color}
      />
      <Container
        maxWidth="800px"
        sx={{
          backgroundColor: "white",
          padding: "20px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          border: "1px solid #CFD8DC",
        }}
      >
        <form onSubmit={handleSubmit}>
          {isAdmin && (
            <Box mb={2} display="flex" alignItems="center" gap={2}>
              <label style={{ fontWeight: 600 }}>Advanced Mode (System Template)</label>
              <input 
                type="checkbox" 
                checked={isAdvancedMode} 
                onChange={(e) => setIsAdvancedMode(e.target.checked)} 
                style={{ width: "18px", height: "18px" }}
              />
            </Box>
          )}

          {isAdvancedMode && (
            <Box mb={2}>
              <label style={{ marginBottom: 3, fontWeight: 600 }}>Plan Access</label>
              <select
                value={planRequired}
                onChange={(e) => setPlanRequired(e.target.value)}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "6px",
                  borderRadius: "4px",
                  border: "1px solid #e0e0e0",
                }}
              >
                <option value={1}>Free</option>
                <option value={2}>Plus</option>
                <option value={3}>Pro</option>
                <option value={4}>Unlimited</option>
              </select>
            </Box>
          )}

          <Box mb={2}>
            <label style={{ marginBottom: 3, fontWeight: 600 }}>
              Template Name
            </label>
            {/* <TextField
              fullWidth
              value={template_name}
              onChange={(e) => setTemplateName(e.target.value)}
              sx={{ marginBottom: 1, height: { xs: 30, sm: 38, md: 45 } }}
            /> */}

            <input
              type="text"
              value={template_name}
              onChange={(e) => setTemplateName(e.target.value)}
              style={{
                display: "block",
                width: "100%",
                padding: "6px",
                borderRadius: "4px",
                border: "1px solid #e0e0e0",
              }}
            />
          </Box>

          <Box mb={2}>
            <label style={{ marginBottom: 3, fontWeight: 600 }}>Subject</label>
            {/* <TextField
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={{ marginBottom: 1, height: { xs: 30, sm: 38, md: 45 } }}
            /> */}
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              style={{
                display: "block",
                width: "100%",
                padding: "6px",
                borderRadius: "4px",
                border: "1px solid #e0e0e0",
              }}
            />
          </Box>

          <Box mb={2} sx={{ position: "relative" }}>
            <label style={{ marginBottom: 3, fontWeight: 600 }}>Message</label>
            <ReactQuill
              ref={quillRef}
              value={richContent}
              onChange={handleEditorChange} 
              modules={modules}
              style={{
                height: "200px",
                marginBottom: "20px",
                fontSize: { xs: "8px", sm: "12px" },
                borderRadius: "8px",
              }}
            />
            {suggestionQuery !== null && filteredFillers.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: cursorPos.top + 30, // 30 for the toolbar height approx
                  left: cursorPos.left,
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                  zIndex: 1000,
                  maxHeight: "150px",
                  overflowY: "auto",
                  minWidth: "150px"
                }}
              >
                {filteredFillers.map((filler, idx) => (
                  <Box
                    key={idx}
                    onClick={() => handleSelectSuggestion(filler.value)}
                    sx={{
                      padding: "8px 12px",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#f0f0f0" },
                      fontSize: "14px",
                      color: "#453c72",
                      fontWeight: "500"
                    }}
                  >
                    {`{{${filler.value}}}`}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: { xs: "100px", sm: "60px" },
            }}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#453c72",
                fontSize: { xs: "14px", sm: "12px" },
              }}
            >
              {loading ? "Submitting..." : "Submit"}
            </Button>
          </Box>

          {/* {statusMessage && <p>{statusMessage}</p>} */}
        </form>
      </Container>
    </div>
  );
};

export default Template;
